import React, { Dispatch, SetStateAction, useEffect } from 'react';
import { Dropdown } from 'react-bootstrap';
import { FaExclamationCircle } from 'react-icons/fa';

import { UseOutletContextTreasury } from '../Treasury';
import { StateLanguage } from '../../../../languages/config/StateLanguage';
import { TreasuryAccount } from '../../../../models/treasury.account.model';
import { AccountingAccount } from '../../../../models/accounting.account.model';
import { GlobalSwal } from '../../../../styles/swal.style';
import { legendInvalid, legendValid, legendIconValidRequired, legendValidInvalidRestart, legendIconValidInvalidRestart } from '../../../../utils/legend.data.util';
import { uploadTooltip } from '../../../../utils/tooltip.util';
import { evaluateLegendOptional, evaluateLegendOptionalIcon, evaluateLegendRequired, evaluateLegendRequiredIcon } from '../../../../scripts/validate.legend.script';
import { expressions } from '../../../../libraries/regular.expressions.library';
import { treasuryAccountTypeValue, treasuryTypeList } from '../../../../libraries/treasury.account.type.library';
import { modalShow } from '../../../../utils/modal.util';
import { prefixLegend } from '../../../../scripts/name.legend.script';
import { formatAccountingAccount } from '../../../../scripts/format.value.script';

import AppLegend from '../../../../components/element/Legend';
import AppModalAccountingAccountCreate from '../../../../components/modal/ModalAccountingAccountCreate';
import AppInputCountry from '../../../../components/element/InputCountry';
import iconCard from '../../../../assets/themes/icons/card-secret-icon.svg';
import iconBank from '../../../../assets/themes/icons/bank-ico.svg';
import iconToken from '../../../../assets/themes/icons/token-icon.svg';

export interface AppFormTreasuryAccountProps {
  component?: string,
  treasuryAccount?: TreasuryAccount,
  typeTreasuryAccount?: {value: string, valid: boolean},
  accountingAccount?: {value: string, valid: boolean, object: any},
  nameAccount: {value: string, valid: boolean},
  iban: {value: string, valid: boolean},
  bban: {value: string, valid: boolean},
  bic: {value: string, valid: boolean},
  membership: {value: string, valid: boolean},
  qrIban: {value: string, valid: boolean},
  reference: {value: string, valid: boolean},
  nameHolder: {value: string, valid: boolean},
  street: {value: string, valid: boolean},
  streetNumber: {value: string, valid: boolean},
  additional: {value: string, valid: boolean},
  administrativeArea: {value: string, valid: boolean},
  city: {value: string, valid: boolean},
  zipCode: {value: string, valid: boolean},
  country: {value: string, valid: boolean},
  options: {is_default: boolean, is_reference: boolean, is_holder: boolean},
  setTypeTreasuryAccount?: Dispatch<SetStateAction<{value: string, valid: boolean}>>,
  setAccountingAccount?: Dispatch<SetStateAction<{value: string, valid: boolean, object: any}>>,
  setNameAccount: Dispatch<SetStateAction<{value: string, valid: boolean}>>,
  setIban: Dispatch<SetStateAction<{value: string, valid: boolean}>>,
  setBban: Dispatch<SetStateAction<{value: string, valid: boolean}>>,
  setBic: Dispatch<SetStateAction<{value: string, valid: boolean}>>,
  setMembership: Dispatch<SetStateAction<{value: string, valid: boolean}>>,
  setQrIban: Dispatch<SetStateAction<{value: string, valid: boolean}>>,
  setReference: Dispatch<SetStateAction<{value: string, valid: boolean}>>,
  setNameHolder: Dispatch<SetStateAction<{value: string, valid: boolean}>>,
  setStreet: Dispatch<SetStateAction<{value: string, valid: boolean}>>,
  setStreetNumber: Dispatch<SetStateAction<{value: string, valid: boolean}>>,
  setAdditional: Dispatch<SetStateAction<{value: string, valid: boolean}>>,
  setAdministrativeArea: Dispatch<SetStateAction<{value: string, valid: boolean}>>,
  setCity: Dispatch<SetStateAction<{value: string, valid: boolean}>>,
  setZipCode: Dispatch<SetStateAction<{value: string, valid: boolean}>>,
  setCountry: Dispatch<SetStateAction<{value: string, valid: boolean}>>,
  setOptions: Dispatch<SetStateAction<{is_default: boolean, is_reference: boolean, is_holder: boolean}>>
};

const AppFormTreasuryAccount: React.FunctionComponent<AppFormTreasuryAccountProps> = ({component, treasuryAccount, typeTreasuryAccount, accountingAccount, nameAccount, iban, bban, bic, membership, qrIban, reference, nameHolder, street, streetNumber, additional, administrativeArea, city, zipCode, country, options, setTypeTreasuryAccount, setAccountingAccount, setNameAccount, setIban, setBban, setBic, setMembership, setQrIban, setReference, setNameHolder, setStreet, setStreetNumber, setAdditional, setAdministrativeArea, setCity, setZipCode, setCountry, setOptions}) => {
  const {companyForUser, treasuryAccountsForCompany, accountingCashAccountsForCompany, accountingPostalAccountsForCompany, accountingBankAccountsForCompany, accountingCurrentAccountAccountsForCompany, setAccountingCashAccountsForCompany, setAccountingPostalAccountsForCompany, setAccountingBankAccountsForCompany, setAccountingCurrentAccountAccountsForCompany, loadAccountingAccountsForCompany} = UseOutletContextTreasury()
  const {lang} = StateLanguage()

  const handleChangeTypeTreasuryAccount = (item: string) => { 
    if (setTypeTreasuryAccount && setAccountingAccount) {
      setTypeTreasuryAccount({value: item, valid: true})
      legendValid(`${prefixLegend(component)}legend-type-account-required`)

      if (accountingCashAccountsForCompany && treasuryAccountsForCompany && !treasuryAccountsForCompany.some((temp_item) => (temp_item.type === 'cash')) && item === 'cash') {
        let accountingCashAccount = accountingCashAccountsForCompany.find((temp_item) => (temp_item.code === '1000')) as AccountingAccount
        setAccountingAccount({value: accountingCashAccount.id, object: accountingCashAccount, valid: true})
        legendValid(`${prefixLegend(component)}legend-accounting-account-required`)
      } else {
        setAccountingAccount({value: '', valid: false, object: null})
        legendValidInvalidRestart(`${prefixLegend(component)}legend-accounting-account-required`)
      }
    }
  }

  const handleChangeAccoutingAccount = (item: AccountingAccount) => {
    if (item.available) {
      if (accountingAccount && setAccountingAccount) {
        setAccountingAccount({value: item.id, valid: true, object: item})
        legendValid(`${prefixLegend(component)}legend-accounting-account-required`)
      }
    } else {
      GlobalSwal.fire({
        text: lang.labels.accountingAccountAlreadySelected,
        icon: 'error',
        confirmButtonText: lang.labels.okGotIt
      })
    }
  }

  const handleChangeNameAccount = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setNameAccount({...nameAccount, value: event.target.value})
  }

  const handleChangeIban = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setIban({...iban, value: event.target.value})
  }

  const handleChangeBban = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setBban({...bban, value: event.target.value})
  }

  const handleChangeBic = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setBic({...bic, value: event.target.value})
  }

  const handleChangeMembership = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setMembership({...membership, value: event.target.value})
  }

  const handleChangeQrIban = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setQrIban({...qrIban, value: event.target.value})
  }

  const handleChangeReference = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setReference({...reference, value: event.target.value})
  }

  const handleChangeNameHolder = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setNameHolder({...nameHolder, value: event.target.value})
  }

  const handleChangeStreet = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setStreet({...street, value: event.target.value})
  }

  const handleChangeStreetNumber = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setStreetNumber({...streetNumber, value: event.target.value})
  }

  const handleChangeAdditional = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setAdditional({...additional, value: event.target.value})
  }

  const handleChangeAdministrativeArea = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setAdministrativeArea({...administrativeArea, value: event.target.value})
  }

  const handleChangeCity = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setCity({...city, value: event.target.value})
  }

  const handleChangeZipCode = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setZipCode({...zipCode, value: event.target.value})
  }

  const handleChangeOptionDefault = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setOptions({...options, is_default: event.target.checked})
  }

  const handleChangeOptionReference = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setOptions({...options, is_reference: event.target.checked})
  }

  const handleChangeOptionHolder = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setOptions({...options, is_holder: event.target.checked})
    if (event.target.checked) {
      startAccountHolder()
    } else {
      resetAccountHolder()
    }
    restartForm()
  }

  const validateNameAccount = () => {
    evaluateLegendRequired(expressions.name, nameAccount, setNameAccount, `${prefixLegend(component)}legend-name-account-valid`, `${prefixLegend(component)}legend-name-account-required`)
  }

  const validateIban = () => {
    evaluateLegendOptional(expressions.iban, iban, setIban, `${prefixLegend(component)}legend-iban-valid`)
  }

  const validateBban = () => {
    evaluateLegendOptional(expressions.bban, bban, setBban, `${prefixLegend(component)}legend-bban-valid`)
  }

  const validateBic = () => {
    evaluateLegendOptional(expressions.bic, bic, setBic, `${prefixLegend(component)}legend-bic-valid`)
  }

  const validateMembership = () => {
    evaluateLegendOptional(expressions.membership, membership, setMembership, `${prefixLegend(component)}legend-membership-valid`)
  }

  const validateQrIban = () => {
    evaluateLegendOptional(expressions.qrIban, qrIban, setQrIban, `${prefixLegend(component)}legend-qr-iban-valid`)
  }

  const validateReference = () => {
    if (reference.value.length === 0) {
      setReference({...reference, valid: true})
      setOptions({...options, is_reference: false})
      legendValidInvalidRestart(`${prefixLegend(component)}legend-reference-valid`)
    } else {
      if (expressions.reference.test(reference.value)) {
        setReference({...reference, valid: true})
        setOptions({...options, is_reference: true})
        legendValid(`${prefixLegend(component)}legend-reference-valid`)
      } else {
        setReference({...reference, valid: false})
        setOptions({...options, is_reference: false})
        legendInvalid(`${prefixLegend(component)}legend-reference-valid`)
      }
    }
  }

  const validateNameHolder = () => {
    evaluateLegendRequiredIcon(expressions.name, nameHolder, setNameHolder, `${prefixLegend(component)}input-name-holder`, `${prefixLegend(component)}legend-name-holder-valid`, `${prefixLegend(component)}legend-name-holder-required`)
  }

  const validateStreet = () => {
    evaluateLegendRequiredIcon(expressions.street, street, setStreet, `${prefixLegend(component)}input-street`, `${prefixLegend(component)}legend-street-valid`, `${prefixLegend(component)}legend-street-required`)
  }

  const validateStreetNumber = () => {
    if (streetNumber.value === 'NN' || streetNumber.value === 'nn') {
      setStreetNumber({...streetNumber, valid: true})
      legendIconValidRequired(`${prefixLegend(component)}input-street-number`, `${prefixLegend(component)}legend-street-number-valid`, `${prefixLegend(component)}legend-street-number-required`)
    } else {
      evaluateLegendRequiredIcon(expressions.streetnumber, streetNumber, setStreetNumber, `${prefixLegend(component)}input-street-number`, `${prefixLegend(component)}legend-street-number-valid`, `${prefixLegend(component)}legend-street-number-required`)
    }
  }

  const validateAdditional = () => {
    evaluateLegendOptionalIcon(expressions.text, additional, setAdditional, `${prefixLegend(component)}input-additional`, `${prefixLegend(component)}legend-additional-valid`)
  }

  const validateAdministrativeArea = () => {
    evaluateLegendRequiredIcon(expressions.location, administrativeArea, setAdministrativeArea, `${prefixLegend(component)}input-administrative-area`, `${prefixLegend(component)}legend-administrative-area-valid`, `${prefixLegend(component)}legend-administrative-area-required`)
  }

  const validateCity = () => {
    evaluateLegendRequiredIcon(expressions.location, city, setCity, `${prefixLegend(component)}input-city`, `${prefixLegend(component)}legend-city-valid`, `${prefixLegend(component)}legend-city-required`)
  }

  const validateZipCode = () => {
    evaluateLegendRequiredIcon(expressions.zipcode, zipCode, setZipCode, `${prefixLegend(component)}input-zip-code`, `${prefixLegend(component)}legend-zip-code-valid`, `${prefixLegend(component)}legend-zip-code-required`)
  }

  const executeShowModalAccountingAccountCreate = () => {
    modalShow('modal-accounting-account-create')
  }

  function accountingAccounts(type: string): AccountingAccount[] {
    switch (type) {
      case 'cash':
        return accountingCashAccountsForCompany ?? []
      case 'postal':
        return accountingPostalAccountsForCompany ?? []
      case 'bank':
        return accountingBankAccountsForCompany ?? []
      case 'shareholder':
      case 'current_account':
        return accountingCurrentAccountAccountsForCompany ?? []
      default:
        return []
    }
  }

  function setAccountingAccounts(type: string): Dispatch<SetStateAction<AccountingAccount[] | null>> {
    switch (type) {
      case 'cash':
        return setAccountingCashAccountsForCompany ?? null
      case 'postal':
        return setAccountingPostalAccountsForCompany ?? null
      case 'bank':
        return setAccountingBankAccountsForCompany ?? null
      case 'shareholder':
      case 'current_account':
        return setAccountingCurrentAccountAccountsForCompany ?? null
      default:
        return () => {}
    }
  }

  function startAccountHolder() {
    if (companyForUser) {
      setNameHolder({value: companyForUser.name, valid: true})
      setStreet({value: companyForUser.address?.street, valid: true})
      setStreetNumber({value: companyForUser.address?.number, valid: true})
      setAdditional({value: companyForUser.address?.additional, valid: true})
      setAdministrativeArea({value: companyForUser.address?.administrative_area, valid: true})
      setCity({value: companyForUser.address?.city, valid: true})
      setZipCode({value: companyForUser.address?.zip_code, valid: true})
      setCountry({value: companyForUser.address?.country, valid: true})
    }
  }

  function resetAccountHolder() {
    if (treasuryAccount) {
      setNameHolder({value: treasuryAccount.account_holder.full_name, valid: true})
      setStreet({value: treasuryAccount.account_holder.address.street, valid: true})
      setStreetNumber({value: treasuryAccount.account_holder.address.number, valid: true})
      setAdditional({value: treasuryAccount.account_holder.address.additional, valid: true})
      setAdministrativeArea({value: treasuryAccount.account_holder.address.administrative_area, valid: true})
      setCity({value: treasuryAccount.account_holder.address.city, valid: true})
      setZipCode({value: treasuryAccount.account_holder.address.zip_code, valid: true})
      setCountry({value: treasuryAccount.account_holder.address.country, valid: true})
    } else {
      setNameHolder({value: '', valid: false})
      setStreet({value: '', valid: false})
      setStreetNumber({value: '', valid: false})
      setAdditional({value: '', valid: false})
      setAdministrativeArea({value: '', valid: false})
      setCity({value: '', valid: false})
      setZipCode({value: '', valid: false})
      setCountry({value: 'CH', valid: true})
    }
  }

  function restartForm() {
    legendIconValidInvalidRestart(`${prefixLegend(component)}input-name-holder`, `${prefixLegend(component)}legend-name-holder-valid`)
    legendIconValidInvalidRestart(`${prefixLegend(component)}input-name-holder`, `${prefixLegend(component)}legend-name-holder-required`)
    legendIconValidInvalidRestart(`${prefixLegend(component)}input-street`, `${prefixLegend(component)}legend-street-valid`)
    legendIconValidInvalidRestart(`${prefixLegend(component)}input-street`, `${prefixLegend(component)}legend-street-required`)
    legendIconValidInvalidRestart(`${prefixLegend(component)}input-street-number`, `${prefixLegend(component)}legend-street-number-valid`)
    legendIconValidInvalidRestart(`${prefixLegend(component)}input-street-number`, `${prefixLegend(component)}legend-street-number-required`)
    legendIconValidInvalidRestart(`${prefixLegend(component)}input-administrative-area`, `${prefixLegend(component)}legend-administrative-area-valid`)
    legendIconValidInvalidRestart(`${prefixLegend(component)}input-administrative-area`, `${prefixLegend(component)}legend-administrative-area-required`)
    legendIconValidInvalidRestart(`${prefixLegend(component)}input-city`, `${prefixLegend(component)}legend-city-valid`)
    legendIconValidInvalidRestart(`${prefixLegend(component)}input-city`, `${prefixLegend(component)}legend-city-required`)
    legendIconValidInvalidRestart(`${prefixLegend(component)}input-zip-code`, `${prefixLegend(component)}legend-zip-code-valid`)
    legendIconValidInvalidRestart(`${prefixLegend(component)}input-zip-code`, `${prefixLegend(component)}legend-zip-code-required`)
    legendValidInvalidRestart(`${prefixLegend(component)}legend-country-required`)
  }

  useEffect( () => {
    if (component) {
      resetAccountHolder()
    } else {
      setOptions({is_default: (treasuryAccountsForCompany?.length === 0), is_reference: (reference.valid && reference.value.length > 0), is_holder: (companyForUser !== null)})
      startAccountHolder()
    }

    uploadTooltip()
    restartForm()

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyForUser, treasuryAccountsForCompany])

  return (
    <>
      { companyForUser && treasuryAccountsForCompany && accountingCashAccountsForCompany && accountingPostalAccountsForCompany && accountingBankAccountsForCompany &&
        <div className="form d-flex flex-column flex-lg-row">
          <div className="d-flex flex-equal flex-column flex-row-fluid order-1 me-lg-3">
            { typeTreasuryAccount && accountingAccount &&
              <>
                <div className="row mb-3">
                  <label className="form-label required">{lang.labels.typeOfTreasuryAccount}</label>
                  <div className="fv-row">
                    <Dropdown>
                      <Dropdown.Toggle variant="select2 select2-container select2-container--bootstrap5 select2-container--below select2-container--focus select2-container--open w-100 p-0">
                        <span className="selection">
                          <span className="select2-selection select2-selection--single form-select">
                            <span className="select2-selection__rendered" role="textbox">
                              {typeTreasuryAccount.valid ? treasuryAccountTypeValue(lang, typeTreasuryAccount.value) : lang.labels.selectOption}
                            </span>
                          </span>
                        </span>
                      </Dropdown.Toggle>
                      <Dropdown.Menu variant="select2-container select2-container--bootstrap5 select2-container--open w-100">
                        <span className="select2-dropdown select2-dropdown--below">
                          <span className="select2-results">
                            <ul className="select2-results__options" role="listbox">
                              { treasuryTypeList(lang).map (( (item, index) => { return (
                                <li key={index} className={`select2-results__option select2-results__option--selectable ${item.code === typeTreasuryAccount.value && "select2-results__option--selected"}`} role="option" aria-selected={item.code === typeTreasuryAccount.value}>
                                  <Dropdown.Item bsPrefix="select2-results__option__text" onClick={() => handleChangeTypeTreasuryAccount(item.code)}>{item.display}</Dropdown.Item>
                                </li>
                              )}))}
                            </ul>
                          </span>
                        </span>
                      </Dropdown.Menu>
                    </Dropdown>
                    <AppLegend component={component} attribute={{check: typeTreasuryAccount.valid, name: "type-account", index: null, sub_index: null}} container={{valid: false, required: true, identical: false, exists: false}}></AppLegend>
                  </div>
                </div>
                <div className="row mb-5">
                  <label className="form-label required">{lang.labels.accountingAccount}</label>
                  <div className="fv-row">
                    <Dropdown>
                      <Dropdown.Toggle variant="select2 select2-container select2-container--bootstrap5 select2-container--below select2-container--focus select2-container--open w-100 p-0">
                        <span className="selection">
                          <span className="select2-selection select2-selection--single form-select">
                            <span className="select2-selection__rendered" role="textbox">
                              {accountingAccount.valid ? formatAccountingAccount(accountingAccount.object, companyForUser.language) : lang.labels.selectOption}
                            </span>
                          </span>
                        </span>
                      </Dropdown.Toggle>
                      <Dropdown.Menu variant="select2-container select2-container--bootstrap5 select2-container--open w-100">
                        <span className="select2-dropdown select2-dropdown--below">
                          <span className="select2-results">
                            <ul className="select2-results__options" role="listbox">
                              <li className={`select2-results__option select2-results__option--selectable ${accountingAccounts(typeTreasuryAccount.value).length > 0 && "d-none"}`}>
                                <Dropdown.Item bsPrefix="select2-results__option__text">{lang.labels.noRecordsFound}</Dropdown.Item>
                              </li>
                              <li className={`select2-results__option select2-results__option--selectable ${!typeTreasuryAccount.valid && "d-none"}`}>
                                <Dropdown.Item bsPrefix="select2-results__option__text" onClick={executeShowModalAccountingAccountCreate}>
                                  <div className="d-flex align-items-center">
                                    <span className="svg-icon svg-icon-3 me-2">
                                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                        <rect opacity="0.3" x="2" y="2" width="20" height="20" rx="5" fill="black" />
                                        <rect x="10.8891" y="17.8033" width="12" height="2" rx="1" transform="rotate(-90 10.8891 17.8033)" fill="black" />
                                        <rect x="6.01041" y="10.9247" width="12" height="2" rx="1" fill="black" />
                                      </svg>
                                    </span>
                                    {lang.labels.addAccountingAccount}
                                  </div>
                                </Dropdown.Item>
                              </li>
                              { accountingAccounts(typeTreasuryAccount.value).map (( (item, index) => { return (
                                <li key={index} className={`select2-results__option select2-results__option--selectable ${item.id === accountingAccount.value && "select2-results__option--selected"}`} role="option" aria-selected={item.id === accountingAccount.value}>
                                  <Dropdown.Item bsPrefix={`select2-results__option__text text-${item.available ? "success" : "danger fs-8"}`} onClick={() => handleChangeAccoutingAccount(item)}>{formatAccountingAccount(item, companyForUser.language)}</Dropdown.Item>
                                </li>
                              )}))}
                            </ul>
                          </span>
                        </span>
                      </Dropdown.Menu>
                    </Dropdown>
                    <AppLegend component={component} attribute={{check: typeTreasuryAccount.valid, name: "accounting-account", index: null, sub_index: null}} container={{valid: false, required: true, identical: false, exists: false}}></AppLegend>
                  </div>
                </div>
              </>
            }
            <div className="row mb-3">
              <label className="form-label required">{lang.labels.nameOfAccount}</label>
              <div className="fv-row">
                <div className="position-relative">
                  <input className="form-control pe-13" type="text" autoComplete="off" name="name-account" value={nameAccount.value} onChange={handleChangeNameAccount} onKeyUp={validateNameAccount} onBlur={validateNameAccount} />
                  <div className="position-absolute translate-middle-y top-50 end-0 me-3">
                    <img src={iconBank} alt={lang.labels.bank} className="h-20px" />
                  </div>  
                </div>
                <AppLegend component={component} attribute={{check: nameAccount.valid, name: "name-account", index: null, sub_index: null}} container={{valid: true, required: true, identical: false, exists: false}}></AppLegend>
              </div>
            </div>
            <div className="row mb-3">
              <label className="form-label">
                <div className="d-flex align-items-center">
                  {lang.labels.iban}
                  <i className="ms-2 fs-8" data-bs-toggle="tooltip" data-bs-trigger="hover" data-bs-original-title={lang.labels.internationalBankAccountNumber}><FaExclamationCircle /></i>
                </div>
              </label>
              <div className="fv-row">
                <div className="position-relative">
                  <input id={`${prefixLegend(component)}input-iban`} className="form-control pe-13" type="text" autoComplete="off" name="iban" value={iban.value} onChange={handleChangeIban} onKeyUp={validateIban} onBlur={validateIban} />
                  <div className="position-absolute translate-middle-y top-50 end-0 me-3">
                    <img src={iconCard} alt={lang.labels.card} className="h-20px" />
                  </div>  
                </div>
                <AppLegend component={component} attribute={{check: iban.valid, name: "iban", index: null, sub_index: null}} container={{valid: true, required: false, identical: false, exists: false}}></AppLegend>
              </div>
            </div>
            <div className="row mb-3">
              <label className="form-label">
                <div className="d-flex align-items-center">
                  {lang.labels.bban}
                  <i className="ms-2 fs-8" data-bs-toggle="tooltip" data-bs-trigger="hover" data-bs-original-title={lang.labels.basicBankAccountNumber}><FaExclamationCircle /></i>
                </div>
              </label>
              <div className="fv-row">
              <div className="position-relative">
                <input id={`${prefixLegend(component)}input-bban`} className="form-control pe-13" type="text" autoComplete="off" name="bban" value={bban.value} onChange={handleChangeBban} onKeyUp={validateBban} onBlur={validateBban} />
                <div className="position-absolute translate-middle-y top-50 end-0 me-3">
                  <img src={iconCard} alt={lang.labels.card} className="h-20px" />
                </div>
              </div>
              <AppLegend component={component} attribute={{check: bban.valid, name: "bban", index: null, sub_index: null}} container={{valid: true, required: false, identical: false, exists: false}}></AppLegend>
              </div>
            </div>
            <div className="row mb-3">
              <label className="form-label">
                <div className="d-flex align-items-center">
                  {lang.labels.bicSwift}
                  <i className="ms-2 fs-8" data-bs-toggle="tooltip" data-bs-trigger="hover" data-bs-original-title={lang.labels.bankIdentifierCode}><FaExclamationCircle /></i>
                </div>
              </label>
              <div className="fv-row">
                <div className="position-relative">
                  <input id={`${prefixLegend(component)}input-bic`} className="form-control pe-13" type="text" autoComplete="off" name="bic" value={bic.value} onChange={handleChangeBic} onKeyUp={validateBic} onBlur={validateBic} />
                  <div className="position-absolute translate-middle-y top-50 end-0 me-3">
                    <img src={iconCard} alt={lang.labels.card} className="h-20px" />
                  </div>
                </div>
                <AppLegend component={component} attribute={{check: bic.valid, name: "bic", index: null, sub_index: null}} container={{valid: true, required: false, identical: false, exists: false}}></AppLegend>
              </div>
            </div>
            <div className="row mb-3">
              <label className="form-label">
                <div className="d-flex align-items-center">
                  {lang.labels.membership}
                  <i className="ms-2 fs-8" data-bs-toggle="tooltip" data-bs-trigger="hover" data-bs-original-title={lang.labels.codeOfBelongingToABankingOrganization}><FaExclamationCircle /></i>
                </div>
              </label>
              <div className="fv-row">
                <div className="position-relative">
                  <input id={`${prefixLegend(component)}input-membership`} className="form-control pe-13" type="text" autoComplete="off" name="iban" value={membership.value} onChange={handleChangeMembership} onKeyUp={validateMembership} onBlur={validateMembership} />
                  <div className="position-absolute translate-middle-y top-50 end-0 me-3">
                    <img src={iconCard} alt={lang.labels.card} className="h-20px" />
                  </div>  
                </div>
                <AppLegend component={component} attribute={{check: membership.valid, name: "membership", index: null, sub_index: null}} container={{valid: true, required: false, identical: false, exists: false}}></AppLegend>
              </div>
            </div>
            <div className="row mb-3">
              <label className="form-label">
                <div className="d-flex align-items-center">
                  {lang.labels.qrIban}
                  <i className="ms-2 fs-8" data-bs-toggle="tooltip" data-bs-trigger="hover" data-bs-original-title={lang.labels.internationalBankAccountNumberIban}><FaExclamationCircle /></i>
                </div>
              </label>
              <div className="fv-row">
                <div className="position-relative">
                  <input id={`${prefixLegend(component)}input-qr-iban`} className="form-control pe-13" type="text" autoComplete="off" name="iban" value={qrIban.value} onChange={handleChangeQrIban} onKeyUp={validateQrIban} onBlur={validateQrIban} />
                  <div className="position-absolute translate-middle-y top-50 end-0 me-3">
                    <img src={iconCard} alt={lang.labels.card} className="h-20px" />
                  </div>
                </div>
                <AppLegend component={component} attribute={{check: qrIban.valid, name: "qr-iban", index: null, sub_index: null}} container={{valid: true, required: false, identical: false, exists: false}}></AppLegend>
              </div>
            </div>
            <div className="row">
              <label className="form-label">
                <div className="d-flex align-items-center">
                  {lang.labels.reference}
                  <i className="ms-2 fs-8" data-bs-toggle="tooltip" data-bs-trigger="hover" data-bs-original-title={lang.labels.informationAdditional}><FaExclamationCircle /></i>
                </div>
              </label>
              <div className="fv-row">
                <div className="position-relative">
                  <input id={`${prefixLegend(component)}input-reference`} className="form-control pe-13" type="text" autoComplete="off" name="reference" value={reference.value} onChange={handleChangeReference} onKeyUp={validateReference} onBlur={validateReference} />
                  <div className="position-absolute translate-middle-y top-50 end-0 me-3">
                    <img src={iconToken} alt={lang.labels.card} className="h-20px" />
                  </div>
                </div>
                <AppLegend component={component} attribute={{check: reference.valid, name: "reference", index: null, sub_index: null}} container={{valid: true, required: false, identical: false, exists: false}}></AppLegend>
              </div>
            </div>
          </div>
          <div className="d-flex flex-equal flex-column flex-row-fluid order-2 ms-lg-3">
            <div className="mb-5">
              { !component &&
                <>
                  <div className="separator separator-dashed my-2"></div>
                  <div className="d-flex flex-stack">
                    <div className="d-flex flex-column">
                      <label className="d-flex align-items-center">
                        <span className="fw-bolder">{lang.labels.default}</span>
                        <i className="ms-2 fs-9" data-bs-toggle="tooltip" data-bs-trigger="hover" data-bs-original-title={lang.labels.isNecessaryToHaveAtLeastTwoTreasuryAccountsToActivateOption}><FaExclamationCircle /></i>
                      </label>
                      <div className="fs-7 text-secondary">{lang.labels.selectThisTreasuryAccountAsTheMainOne}</div>
                    </div>
                    <div className="d-flex justify-content-end">
                      <div className="form-check form-check-solid form-check-custom form-switch">
                        <input className="form-check-input w-50px h-30px" type="checkbox" disabled={!(treasuryAccountsForCompany.length > 0)} checked={options.is_default} onChange={handleChangeOptionDefault} />
                      </div>
                    </div>
                  </div>
                </>
              }
              <div className="separator separator-dashed my-2"></div>
              <div className="d-flex flex-stack">
                <div className="d-flex flex-column">
                  <label className="d-flex align-items-center">
                    <span className="fw-bolder">{lang.labels.reference}</span>
                    <i className="ms-2 fs-9" data-bs-toggle="tooltip" data-bs-trigger="hover" data-bs-original-title={lang.labels.isNecessaryToHaveReferenceToActivateOption}><FaExclamationCircle /></i>
                  </label>
                  <div className="fs-7 text-secondary">{lang.labels.useReferenceWithinQR}</div>
                </div>
                <div className="d-flex justify-content-end">
                  <div className="form-check form-check-solid form-check-custom form-switch">
                    <input className="form-check-input w-50px h-30px" type="checkbox" disabled={!(reference.valid && reference.value.length > 0)} checked={options.is_reference} onChange={handleChangeOptionReference} />
                  </div>
                </div>
              </div>
              <div className="separator separator-dashed my-2"></div>
              <div className="d-flex flex-stack">
                <div className="d-flex flex-column">
                  <label className="d-flex align-items-center">
                    <span className="fw-bolder">{lang.labels.bankAccountHolder}</span>
                    <i className="ms-2 fs-9" data-bs-toggle="tooltip" data-bs-trigger="hover" data-bs-original-title={`${lang.labels.company}: ${companyForUser.name}`}><FaExclamationCircle /></i>
                  </label>
                  <div className="fs-7 text-secondary">{lang.labels.isCompanyOwnerOfBankAccount}</div>
                </div>
                <div className="d-flex justify-content-end">
                  <div className="form-check form-check-solid form-check-custom form-switch">
                    <input className="form-check-input w-50px h-30px" type="checkbox" disabled={!companyForUser} checked={options.is_holder} onChange={handleChangeOptionHolder} />
                  </div>
                </div>
              </div>
              <div className="separator separator-dashed my-2"></div>
            </div>
            <div className="border border-dashed border-gray-300 rounded-1 bg-lighten p-3">
              <div className="fv-row mb-3">
                <label className="form-label fs-7 mb-1 d-flex align-items-center">
                  <span className="required">{lang.labels.fullName}</span>
                  <i className="ms-2 fs-9" data-bs-toggle="tooltip" data-bs-trigger="hover" data-bs-original-title={lang.labels.fullNameBankAccountHolder}><FaExclamationCircle /></i>
                </label>
                <input id={`${prefixLegend(component)}input-name-holder`} className="form-control form-control-sm" type="text" autoComplete="off" name="name-holder" disabled={options.is_holder} value={nameHolder.value} onChange={handleChangeNameHolder} onKeyUp={validateNameHolder} onBlur={validateNameHolder} />
                <AppLegend component={component} attribute={{check: nameHolder.valid, name: "name-holder", index: null, sub_index: null}} container={{valid: true, required: true, identical: false, exists: false}}></AppLegend>
              </div>
              <div className="d-flex align-self-center mb-3">
                <div className="flex-grow-1 w-75 me-2">
                  <label className="form-label fs-7 mb-1 required">{lang.labels.street}</label>
                  <input id={`${prefixLegend(component)}input-street`} className="form-control form-control-sm" type="text" autoComplete="off" name="street" disabled={options.is_holder} value={street.value} onChange={handleChangeStreet} onKeyUp={validateStreet} onBlur={validateStreet} />
                  <AppLegend component={component} attribute={{check: street.valid, name: "street", index: null, sub_index: null}} container={{valid: true, required: true, identical: false, exists: false}}></AppLegend>
                </div>
                <div className="flex-shrink-0 w-25">
                  <label className="form-label fs-7 mb-1 d-flex align-items-center">
                    <span className="required">{lang.labels.number}</span>
                    <i className="ms-2 fs-9" data-bs-toggle="tooltip" data-bs-trigger="hover" data-bs-original-title={lang.labels.ifAddressDoesNotHaveANumber}><FaExclamationCircle /></i>
                  </label>
                  <input id={`${prefixLegend(component)}input-street-number`} className="form-control form-control-sm" type="text" autoComplete="off" name="street-number" disabled={options.is_holder} value={streetNumber.value} onChange={handleChangeStreetNumber} onKeyUp={validateStreetNumber} onBlur={validateStreetNumber} />
                  <AppLegend component={component} attribute={{check: streetNumber.valid, name: "street-number", index: null, sub_index: null}} container={{valid: true, required: true, identical: false, exists: false}}></AppLegend>
                </div>
              </div>
              <div className="fv-row mb-3">
                <label className="form-label fs-7 mb-1 d-flex align-items-center">
                  {lang.labels.additional}
                  <i className="ms-2 fs-9" data-bs-toggle="tooltip" data-bs-trigger="hover" data-bs-original-title={lang.labels.ifNecessaryEnterSomethingReferentialRegardingAddress}><FaExclamationCircle /></i>
                </label>
                <input id={`${prefixLegend(component)}input-additional`} className="form-control form-control-sm" type="text" autoComplete="off" name="additional" disabled={options.is_holder} value={additional.value} onChange={handleChangeAdditional} onKeyUp={validateAdditional} onBlur={validateAdditional} />
                <AppLegend component={component} attribute={{check: additional.valid, name: "additional", index: null, sub_index: null}} container={{valid: true, required: false, identical: false, exists: false}}></AppLegend>
              </div>
              <div className="fv-row mb-3">
                <label className="form-label fs-7 mb-1 required">{lang.labels.administrativeArea}</label>
                <input id={`${prefixLegend(component)}input-administrative-area`} className="form-control form-control-sm" type="text" autoComplete="off" name="administrative-area" disabled={options.is_holder} value={administrativeArea.value} onChange={handleChangeAdministrativeArea} onKeyUp={validateAdministrativeArea} onBlur={validateAdministrativeArea} />
                <AppLegend component={component} attribute={{check: administrativeArea.valid, name: "administrative-area", index: null, sub_index: null}} container={{valid: true, required: true, identical: false, exists: false}}></AppLegend>
              </div>
              <div className="d-flex align-self-center mb-3">
                <div className="flex-shrink-0 w-25 me-2">
                  <label className="form-label mb-1 d-flex align-items-center">
                    <span className="required">{lang.labels.zipCode}</span>
                    <i className="ms-2 fs-9" data-bs-toggle="tooltip" data-bs-trigger="hover" data-bs-original-title={lang.labels.enterThePostalCodeOfTheCity}><FaExclamationCircle /></i>
                  </label>
                  <input id={`${prefixLegend(component)}input-zip-code`} className="form-control form-control-sm" type="text" autoComplete="off" name="zip-code" disabled={options.is_holder} value={zipCode.value} onChange={handleChangeZipCode} onKeyUp={validateZipCode} onBlur={validateZipCode} />
                  <AppLegend component={component} attribute={{check: zipCode.valid, name: "zip-code", index: null, sub_index: null}} container={{valid: true, required: true, identical: false, exists: false}}></AppLegend>
                </div>
                <div className="flex-grow-1 w-75">
                  <label className="form-label fs-7 mb-1 required">{lang.labels.city}</label>
                  <input id={`${prefixLegend(component)}input-city`} className="form-control form-control-sm" type="text" autoComplete="off" name="city" disabled={options.is_holder} value={city.value} onChange={handleChangeCity} onKeyUp={validateCity} onBlur={validateCity} />
                  <AppLegend component={component} attribute={{check: city.valid, name: "city", index: null, sub_index: null}} container={{valid: true, required: true, identical: false, exists: false}}></AppLegend>
                </div>
              </div>
              <div className="fv-row">
                <label className="form-label fs-7 mb-1 required">{lang.labels.country}</label>
                <AppInputCountry options={{small: true, disabled: options.is_holder, placeholder: null}} country={country} setCountry={setCountry}></AppInputCountry>
              </div>
            </div>
          </div>
        </div>
      }
      { typeTreasuryAccount && setAccountingAccount &&
        <AppModalAccountingAccountCreate companyForUser={companyForUser} type={typeTreasuryAccount.value} accountingAccounts={accountingAccounts(typeTreasuryAccount.value)} setAccountingAccount={setAccountingAccount} setAccountingAccounts={setAccountingAccounts(typeTreasuryAccount.value)} loadAccountingAccountsForCompany={loadAccountingAccountsForCompany}></AppModalAccountingAccountCreate>
      }
    </>
  )
};

export default AppFormTreasuryAccount;
